import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import 'chart.js/auto';
import { db } from '../firebase';
import { collection, query, getDocs, where, orderBy } from 'firebase/firestore';
import { fetchEvents, getItemEventKey, getItemEventName } from '../utils/events';
import './OrderStats.css';

const COLORS = ['#38bdf8', '#22c55e', '#f59e0b', '#f97316', '#ec4899', '#8b5cf6', '#14b8a6', '#ef4444', '#eab308', '#6366f1'];
const currencyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  maximumFractionDigits: 0,
});
const numberFormatter = new Intl.NumberFormat('es-AR');

const formatDateForLabel = (value) => {
  if (!value) return '';

  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
};

const sumValues = (items) => items.reduce((acc, entry) => acc + Number(entry.value || 0), 0);

const formatMetricValue = (value, valueType = 'number') => (
  valueType === 'currency'
    ? currencyFormatter.format(value || 0)
    : numberFormatter.format(value || 0)
);

const getDateRange = (startDate, endDate) => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T23:59:59.999`);
  return { start, end };
};

const getItemPrice = (item) => {
  const price = item.price ?? item.unitPrice ?? item.unit_price ?? item.finalPrice ?? 0;
  const numericPrice = Number(price);
  return Number.isFinite(numericPrice) ? numericPrice : 0;
};

const addMetric = (map, key, name, value) => {
  if (!key) return;
  const current = map.get(key) || { name, value: 0 };
  current.name = current.name || name;
  current.value += Number(value || 0);
  map.set(key, current);
};

const getSaleCreator = (sale = {}) => {
  const name = sale.createdByName || sale.createdByEmail || sale.createdByUid || 'Sin administrador registrado';
  return {
    key: sale.createdByUid || sale.createdByEmail || name,
    name,
    email: sale.createdByEmail || '',
    role: sale.createdByRole || '',
  };
};

const getSaleItems = (sale = {}) => (
  Array.isArray(sale.products)
    ? sale.products
    : Array.isArray(sale.cartItems)
      ? sale.cartItems
      : []
);

const getQrReader = (log = {}) => {
  const name = log.scannedByName || log.scannedByEmail || log.scannedByUid || 'Sin lector registrado';
  return {
    key: log.scannedByUid || log.scannedByEmail || name,
    name,
    email: log.scannedByEmail || '',
    role: log.scannedByRole || '',
  };
};

const mapQrReaderRows = (logs) => {
  const readers = new Map();
  logs.forEach((log) => {
    if (log.action !== 'markUsed') return;
    const reader = getQrReader(log);
    const current = readers.get(reader.key) || {
      key: reader.key,
      name: reader.name,
      email: reader.email,
      role: reader.role,
      checkedCount: 0,
      products: new Map(),
    };
    const productName = log.model || 'QR sin producto';
    const productCollection = log.productCollection || 'products';
    const productKey = `${productCollection}:${productName}`;
    const product = current.products.get(productKey) || {
      key: productKey,
      name: productName,
      type: productCollection === 'otherProducts' ? 'Consumo' : 'Entrada',
      checkedCount: 0,
    };
    product.checkedCount += 1;
    current.checkedCount += 1;
    current.products.set(productKey, product);
    readers.set(reader.key, current);
  });

  return Array.from(readers.values())
    .map((reader) => ({
      ...reader,
      products: Array.from(reader.products.values()).sort((a, b) => b.checkedCount - a.checkedCount),
    }))
    .sort((a, b) => b.checkedCount - a.checkedCount);
};

const mapToChartData = (map) => (
  Array.from(map.values())
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value)
);

const ChartCard = ({ title, eyebrow, data, valueType = 'number' }) => {
  const total = sumValues(data);
  const formattedTotal = formatMetricValue(total, valueType);
  const hasChartData = data.some((entry) => Number(entry.value || 0) > 0);
  const chartData = useMemo(() => ({
    labels: data.map((entry) => entry.name),
    datasets: [
      {
        data: data.map((entry) => Number(entry.value || 0)),
        backgroundColor: data.map((_, index) => COLORS[index % COLORS.length]),
        borderColor: 'rgba(15, 23, 42, 0.88)',
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  }), [data]);
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '52%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#cbd5e1',
          boxWidth: 10,
          padding: 14,
          usePointStyle: true,
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = Number(context.raw || 0);
            return `${label}: ${formatMetricValue(value, valueType)}`;
          },
        },
      },
    },
  }), [valueType]);

  return (
    <section className="orderStats-chartCard">
      <div className="orderStats-chartHeader">
        <div>
          <span>{eyebrow}</span>
          <h4>{title}</h4>
        </div>
        <strong>{formattedTotal}</strong>
      </div>

      {hasChartData ? (
        <div className="orderStats-chartCanvas">
          <Doughnut data={chartData} options={chartOptions} />
        </div>
      ) : (
        <div className="orderStats-emptyChart">
          <span>Sin datos para este periodo</span>
        </div>
      )}
    </section>
  );
};

const EventBarsCard = ({ events }) => {
  const hasData = events.some((event) => (
    event.ticketsSold
    || event.freeEntries
    || event.unitsConsumed
    || event.freeConsumptions
  ));

  const chartData = useMemo(() => ({
    labels: events.map((event) => event.name),
    datasets: [
      {
        label: 'Entradas vendidas',
        data: events.map((event) => event.ticketsSold),
        backgroundColor: '#38bdf8',
        borderRadius: 8,
        stack: 'total',
      },
      {
        label: 'Entradas free',
        data: events.map((event) => event.freeEntries),
        backgroundColor: '#8b5cf6',
        borderRadius: 8,
        stack: 'total',
      },
      {
        label: 'Consumos vendidos',
        data: events.map((event) => event.unitsConsumed),
        backgroundColor: '#22c55e',
        borderRadius: 8,
        stack: 'total',
      },
      {
        label: 'Consumos free',
        data: events.map((event) => event.freeConsumptions),
        backgroundColor: '#f59e0b',
        borderRadius: 8,
        stack: 'total',
      },
    ],
  }), [events]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#cbd5e1',
          boxWidth: 12,
          padding: 14,
          usePointStyle: true,
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${numberFormatter.format(Number(context.raw || 0))}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          color: '#cbd5e1',
          maxRotation: 45,
          minRotation: 0,
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.12)',
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          color: '#cbd5e1',
          precision: 0,
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.12)',
        },
      },
    },
  }), []);

  return (
    <section className="orderStats-chartCard orderStats-chartCard--wide">
      <div className="orderStats-chartHeader">
        <div>
          <span>Eventos</span>
          <h4>Entradas y consumos por evento</h4>
        </div>
        <strong>{numberFormatter.format(events.length)}</strong>
      </div>

      {hasData ? (
        <div className="orderStats-barCanvas">
          <Bar data={chartData} options={chartOptions} />
        </div>
      ) : (
        <div className="orderStats-emptyChart">
          <span>Sin datos para este periodo</span>
        </div>
      )}
    </section>
  );
};

const StaffOperationsBarsCard = ({ cashierRows, readerRows }) => {
  const chartRows = useMemo(() => [
    ...cashierRows.map((row) => ({
      name: `Caja: ${row.name}`,
      sold: row.totalUnits,
      checked: 0,
    })),
    ...readerRows.map((row) => ({
      name: `QR: ${row.name}`,
      sold: 0,
      checked: row.checkedCount,
    })),
  ], [cashierRows, readerRows]);
  const hasData = chartRows.some((row) => row.sold > 0 || row.checked > 0);
  const chartData = useMemo(() => ({
    labels: chartRows.map((row) => row.name),
    datasets: [
      {
        label: 'Unidades vendidas',
        data: chartRows.map((row) => row.sold),
        backgroundColor: '#21e6c1',
        borderRadius: 8,
      },
      {
        label: 'QR validados',
        data: chartRows.map((row) => row.checked),
        backgroundColor: '#8b5cf6',
        borderRadius: 8,
      },
    ],
  }), [chartRows]);
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#cbd5e1', usePointStyle: true },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${numberFormatter.format(Number(context.raw || 0))}`,
        },
      },
    },
    scales: {
      x: { ticks: { color: '#cbd5e1' }, grid: { color: 'rgba(148, 163, 184, 0.12)' } },
      y: { beginAtZero: true, ticks: { color: '#cbd5e1', precision: 0 }, grid: { color: 'rgba(148, 163, 184, 0.12)' } },
    },
  }), []);

  return (
    <section className="orderStats-chartCard orderStats-chartCard--wide">
      <div className="orderStats-chartHeader">
        <div>
          <span>Staff</span>
          <h4>Cajeros vs lectores QR</h4>
        </div>
        <strong>{numberFormatter.format(chartRows.length)}</strong>
      </div>
      {hasData ? (
        <div className="orderStats-barCanvas">
          <Bar data={chartData} options={chartOptions} />
        </div>
      ) : (
        <div className="orderStats-emptyChart">
          <span>Sin ventas o validaciones QR para este periodo</span>
        </div>
      )}
    </section>
  );
};

const OrderStats = () => {
  const getDefaultStartDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  };

  const getDefaultEndDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  };

  const [stats, setStats] = useState({
    entryRevenueByEvent: [],
    consumptionRevenueByEvent: [],
    attendanceByEvent: [],
    topEntries: [],
    topConsumptions: [],
    eventRows: [],
    localAdminRows: [],
    qrReaderRows: [],
    totals: {
      revenue: 0,
      entryRevenue: 0,
      consumptionRevenue: 0,
      ticketsSold: 0,
      freeTicketsCreated: 0,
      freeEntries: 0,
      freeConsumptions: 0,
      attendance: 0,
      unitsConsumed: 0,
    },
  });
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchStats = useCallback(async () => {
    if (!startDate || !endDate) return;

    try {
      const { start, end } = getDateRange(startDate, endDate);
      const [events, onlineSnapshot, localSnapshot, freeSnapshot, qrScanSnapshot] = await Promise.all([
        fetchEvents(db),
        getDocs(query(
          collection(db, 'compras_mercadopago'),
          where('date', '>=', start),
          where('date', '<=', end),
          orderBy('date', 'asc')
        )),
        getDocs(query(
          collection(db, 'ventas_fisicas'),
          where('date', '>=', start),
          where('date', '<=', end),
          orderBy('date', 'asc')
        )),
        getDocs(query(
          collection(db, 'FreeTicket'),
          where('createdAt', '>=', start),
          where('createdAt', '<=', end)
        )),
        getDocs(query(
          collection(db, 'qr_scan_logs'),
          where('scannedAt', '>=', start),
          where('scannedAt', '<=', end),
          orderBy('scannedAt', 'asc')
        )),
      ]);
      const eventsById = new Map(events.map((event) => [event.id, event]));
      const eventSummary = new Map();
      const entryRevenueByEvent = new Map();
      const consumptionRevenueByEvent = new Map();
      const attendanceByEvent = new Map();
      const topEntries = new Map();
      const topConsumptions = new Map();
      const localAdminSummary = new Map();
      const totals = {
        revenue: 0,
        entryRevenue: 0,
        consumptionRevenue: 0,
        ticketsSold: 0,
        freeTicketsCreated: 0,
        freeEntries: 0,
        freeConsumptions: 0,
        attendance: 0,
        unitsConsumed: 0,
      };

      const getEventContext = (item, sale = {}) => {
        const eventFromCollection = item.eventId ? eventsById.get(item.eventId) : null;
        const eventName = item.eventName || sale.eventName || eventFromCollection?.name || getItemEventName(item, sale);
        return {
          key: getItemEventKey({ ...item, eventName }, sale),
          name: eventName,
        };
      };

      const getEventRow = (key, name) => {
        const current = eventSummary.get(key) || {
          key,
          name,
          entryRevenue: 0,
          consumptionRevenue: 0,
          ticketsSold: 0,
          freeTicketsCreated: 0,
          freeEntries: 0,
          freeConsumptions: 0,
          attendance: 0,
          unitsConsumed: 0,
        };
        current.name = current.name || name;
        eventSummary.set(key, current);
        return current;
      };

      const addSaleItem = (item, sale = {}) => {
        const quantity = Number(item.quantity || 0);
        if (!item.model || quantity <= 0) return;

        const collectionName = item.collection || item.productCollection || item.catalogCollection || 'products';
        const amount = quantity * getItemPrice(item);
        const { key, name } = getEventContext({ ...item, collection: collectionName }, sale);
        const row = getEventRow(key, name);

        totals.revenue += amount;

        if (collectionName === 'otherProducts') {
          row.consumptionRevenue += amount;
          row.unitsConsumed += quantity;
          totals.consumptionRevenue += amount;
          totals.unitsConsumed += quantity;
          addMetric(consumptionRevenueByEvent, key, name, amount);
          addMetric(topConsumptions, item.model, item.model, amount);
          return;
        }

        row.entryRevenue += amount;
        row.ticketsSold += quantity;
        totals.entryRevenue += amount;
        totals.ticketsSold += quantity;
        addMetric(entryRevenueByEvent, key, name, amount);
        addMetric(topEntries, item.model, item.model, amount);

        if (item.isUsed) {
          row.attendance += quantity;
          totals.attendance += quantity;
          addMetric(attendanceByEvent, key, name, quantity);
        }
      };

      const addLocalAdminItem = (item, sale = {}) => {
        const quantity = Number(item.quantity || 0);
        if (!item.model || quantity <= 0) return;

        const creator = getSaleCreator(sale);
        const collectionName = item.collection || item.productCollection || item.catalogCollection || 'products';
        const amount = quantity * getItemPrice(item);
        const admin = localAdminSummary.get(creator.key) || {
          key: creator.key,
          name: creator.name,
          email: creator.email,
          role: creator.role,
          salesCount: 0,
          totalAmount: 0,
          totalUnits: 0,
          products: new Map(),
        };
        const productKey = `${collectionName}:${item.model}`;
        const product = admin.products.get(productKey) || {
          key: productKey,
          name: item.model,
          type: collectionName === 'otherProducts' ? 'Consumo' : 'Entrada',
          amount: 0,
          quantity: 0,
        };

        product.amount += amount;
        product.quantity += quantity;
        admin.totalAmount += amount;
        admin.totalUnits += quantity;
        admin.products.set(productKey, product);
        localAdminSummary.set(creator.key, admin);
      };

      onlineSnapshot.docs
        .map((docSnap) => docSnap.data())
        .filter((purchase) => purchase.isPaid !== false || purchase.isConfirmed === true)
        .forEach((purchase) => {
          (purchase.cartItems || []).forEach((item) => addSaleItem(item, purchase));
        });

      localSnapshot.docs
        .map((docSnap) => docSnap.data())
        .filter((sale) => sale.isConfirmed !== false)
        .forEach((sale) => {
          const items = getSaleItems(sale);
          const creator = getSaleCreator(sale);
          const admin = localAdminSummary.get(creator.key) || {
            key: creator.key,
            name: creator.name,
            email: creator.email,
            role: creator.role,
            salesCount: 0,
            totalAmount: 0,
            totalUnits: 0,
            products: new Map(),
          };
          admin.salesCount += 1;
          localAdminSummary.set(creator.key, admin);
          items.forEach((item) => {
            addSaleItem(item, sale);
            addLocalAdminItem(item, sale);
          });
        });

      freeSnapshot.docs
        .map((docSnap) => docSnap.data())
        .forEach((ticket) => {
          (ticket.cartItems || []).forEach((item) => {
            const quantity = Number(item.quantity || 0);
            if (!item.model || quantity <= 0) return;

            const collectionName = item.collection || item.productCollection || item.catalogCollection || 'products';
            const { key, name } = getEventContext({ ...item, collection: collectionName }, ticket);
            const row = getEventRow(key, name);
            row.freeTicketsCreated += quantity;
            totals.freeTicketsCreated += quantity;

            if (collectionName === 'otherProducts') {
              row.freeConsumptions += quantity;
              totals.freeConsumptions += quantity;
            } else {
              row.freeEntries += quantity;
              totals.freeEntries += quantity;
            }

            if (item.isUsed) {
              row.attendance += quantity;
              totals.attendance += quantity;
              addMetric(attendanceByEvent, key, name, quantity);
            }
          });
        });

      setStats({
        entryRevenueByEvent: mapToChartData(entryRevenueByEvent),
        consumptionRevenueByEvent: mapToChartData(consumptionRevenueByEvent),
        attendanceByEvent: mapToChartData(attendanceByEvent),
        topEntries: mapToChartData(topEntries),
        topConsumptions: mapToChartData(topConsumptions),
        eventRows: Array.from(eventSummary.values())
          .sort((a, b) => (b.entryRevenue + b.consumptionRevenue) - (a.entryRevenue + a.consumptionRevenue)),
        localAdminRows: Array.from(localAdminSummary.values())
          .map((admin) => ({
            ...admin,
            products: Array.from(admin.products.values()).sort((a, b) => b.amount - a.amount),
          }))
          .sort((a, b) => b.totalAmount - a.totalAmount),
        qrReaderRows: mapQrReaderRows(qrScanSnapshot.docs.map((docSnap) => docSnap.data())),
        totals,
      });
    } catch (error) {
      console.error('Error fetching event stats:', error);
      throw error;
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        await fetchStats();
      } catch (error) {
        setErrorMessage('No se pudieron cargar las métricas. Revisá la conexión o intentá nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [fetchStats]);

  const totals = stats.totals;
  const topProducts = useMemo(() => (
    [...stats.topEntries, ...stats.topConsumptions]
      .sort((a, b) => b.value - a.value)
      .slice(0, 5)
  ), [stats.topConsumptions, stats.topEntries]);

  const periodLabel = `${formatDateForLabel(startDate)} - ${formatDateForLabel(endDate)}`;
  const averageConsumption = totals.attendance > 0 ? totals.consumptionRevenue / totals.attendance : 0;

  return (
    <div className="orderStats-container">
      <header className="orderStats-hero">
        <div>
          <span className="orderStats-kicker">Panel de métricas</span>
          <h3>Métricas por evento</h3>
          <p>Ingresos de entradas, consumos, asistencia y productos más vendidos por cada evento.</p>
        </div>

        <div className="orderStats-filters" aria-label="Filtros de fecha">
          <label>
            Desde
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="orderStats-filterInput"
            />
          </label>
          <label>
            Hasta
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="orderStats-filterInput"
            />
          </label>
        </div>
      </header>

      <section className="orderStats-summary" aria-label="Resumen del periodo">
        <article className="orderStats-summaryCard orderStats-summaryCard--primary">
          <span>Ingresos totales</span>
          <strong>{currencyFormatter.format(totals.revenue)}</strong>
          <small>{periodLabel}</small>
        </article>
        <article className="orderStats-summaryCard">
          <span>Entradas</span>
          <strong>{currencyFormatter.format(totals.entryRevenue)}</strong>
          <small>{numberFormatter.format(totals.ticketsSold)} vendidas · {numberFormatter.format(totals.freeEntries)} free</small>
        </article>
        <article className="orderStats-summaryCard">
          <span>Consumos</span>
          <strong>{currencyFormatter.format(totals.consumptionRevenue)}</strong>
          <small>{numberFormatter.format(totals.unitsConsumed)} vendidos · {numberFormatter.format(totals.freeConsumptions)} free</small>
        </article>
        <article className="orderStats-summaryCard">
          <span>Ingresos registrados</span>
          <strong>{numberFormatter.format(totals.attendance)}</strong>
          <small>QR marcados como usados</small>
        </article>
        <article className="orderStats-summaryCard">
          <span>Consumo por persona</span>
          <strong>{currencyFormatter.format(averageConsumption)}</strong>
          <small>Consumo dividido por ingresos</small>
        </article>
      </section>

      {errorMessage && (
        <div className="orderStats-alert" role="alert">
          {errorMessage}
        </div>
      )}

      {loading ? (
        <div className="orderStats-loading">
          <span className="orderStats-loader" />
          <p>Cargando métricas...</p>
        </div>
      ) : (
        <>
          <section className="orderStats-insights">
            <div>
              <span className="orderStats-kicker">Ranking</span>
              <h4>Productos con mayor facturación</h4>
            </div>
            {topProducts.length > 0 ? (
              <ol className="orderStats-ranking">
                {topProducts.map((product, index) => (
                  <li key={product.name}>
                    <span>{index + 1}</span>
                    <p>{product.name}</p>
                    <strong>{currencyFormatter.format(product.value)}</strong>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="orderStats-muted">Todavía no hay ventas para mostrar en este rango.</p>
            )}
          </section>

          <section className="orderStats-eventBreakdown">
            <div>
              <span className="orderStats-kicker">Eventos</span>
              <h4>Control por evento</h4>
            </div>
            {stats.eventRows.length > 0 ? (
              <div className="orderStats-eventRows">
                {stats.eventRows.map((event) => (
                  <article key={event.key} className="orderStats-eventRow">
                    <strong>{event.name}</strong>
                    <span>Entradas: {currencyFormatter.format(event.entryRevenue)} · {numberFormatter.format(event.ticketsSold)} vendidas · {numberFormatter.format(event.freeEntries)} free</span>
                    <span>Consumos: {currencyFormatter.format(event.consumptionRevenue)} · {numberFormatter.format(event.unitsConsumed)} vendidos · {numberFormatter.format(event.freeConsumptions)} free</span>
                    <span>Ingresos: {numberFormatter.format(event.attendance)} · Free totales: {numberFormatter.format(event.freeTicketsCreated)}</span>
                  </article>
                ))}
              </div>
            ) : (
              <p className="orderStats-muted">No hay eventos con ventas o ingresos en este rango.</p>
            )}
          </section>

          <section className="orderStats-adminBreakdown">
            <div>
              <span className="orderStats-kicker">Ventas físicas</span>
              <h4>Productos vendidos por administrador</h4>
            </div>
            {stats.localAdminRows.length > 0 ? (
              <div className="orderStats-adminRows">
                {stats.localAdminRows.map((admin) => (
                  <article key={admin.key} className="orderStats-adminRow">
                    <div className="orderStats-adminHead">
                      <div>
                        <strong>{admin.name}</strong>
                        <span>{admin.email || admin.role || 'Sin email registrado'}</span>
                      </div>
                      <div>
                        <strong>{currencyFormatter.format(admin.totalAmount)}</strong>
                        <span>{numberFormatter.format(admin.totalUnits)} unidades · {numberFormatter.format(admin.salesCount)} ventas</span>
                      </div>
                    </div>
                    <div className="orderStats-adminProducts">
                      {admin.products.map((product) => (
                        <div key={product.key} className="orderStats-adminProduct">
                          <span>{product.name}</span>
                          <small>{product.type}</small>
                          <strong>{numberFormatter.format(product.quantity)} u.</strong>
                          <strong>{currencyFormatter.format(product.amount)}</strong>
                        </div>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <p className="orderStats-muted">No hay ventas físicas con productos en este rango.</p>
            )}
          </section>

          <section className="orderStats-adminBreakdown">
            <div>
              <span className="orderStats-kicker">Lectores QR</span>
              <h4>Tickets validados por lector</h4>
            </div>
            {stats.qrReaderRows.length > 0 ? (
              <div className="orderStats-adminRows">
                {stats.qrReaderRows.map((reader) => (
                  <article key={reader.key} className="orderStats-adminRow">
                    <div className="orderStats-adminHead">
                      <div>
                        <strong>{reader.name}</strong>
                        <span>{reader.email || reader.role || 'Sin email registrado'}</span>
                      </div>
                      <div>
                        <strong>{numberFormatter.format(reader.checkedCount)} QR</strong>
                        <span>validados en el periodo</span>
                      </div>
                    </div>
                    <div className="orderStats-adminProducts">
                      {reader.products.map((product) => (
                        <div key={product.key} className="orderStats-adminProduct">
                          <span>{product.name}</span>
                          <small>{product.type}</small>
                          <strong>{numberFormatter.format(product.checkedCount)} QR</strong>
                          <strong>Validado</strong>
                        </div>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <p className="orderStats-muted">No hay validaciones QR registradas en este rango.</p>
            )}
          </section>

          <div className="orderStats-charts">
            <StaffOperationsBarsCard cashierRows={stats.localAdminRows} readerRows={stats.qrReaderRows} />
            <EventBarsCard events={stats.eventRows} />
            <ChartCard
              title="Ingresos de entradas por evento"
              eyebrow="Tickets"
              data={stats.entryRevenueByEvent}
              valueType="currency"
            />
            <ChartCard
              title="Ingresos de consumos por evento"
              eyebrow="Barras y comida"
              data={stats.consumptionRevenueByEvent}
              valueType="currency"
            />
            <ChartCard
              title="Ingresos al evento"
              eyebrow="QR usados"
              data={stats.attendanceByEvent}
            />
            <ChartCard
              title="Consumos más vendidos"
              eyebrow="Consumos"
              data={stats.topConsumptions}
              valueType="currency"
            />
            <ChartCard
              title="Entradas más vendidas"
              eyebrow="Entradas"
              data={stats.topEntries}
              valueType="currency"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default OrderStats;
